/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useRef } from 'react';
import { Controller } from 'react-hook-form';
// eslint-disable-next-line import/no-unresolved
import ReactQuillLib from 'react-quill';
// eslint-disable-next-line import/no-unresolved
import 'react-quill/dist/quill.snow.css';

import { ErrorWarningIcon } from '@icons';

import { ReactQuillComponent } from './types';

const ReactQuill: ReactQuillComponent = (props) => {
  const {
    className = '',
    control,
    disabled = false,
    errorMessage = '',
    id,
    isMandatory = false,
    isOptional = false,
    label = '',
    name,
    showErrorMessage = true,
    isLoading = false,
    placeholder = '',
    height = '150px',
    onImageUpload,
  } = props;

  const quillRef = useRef<ReactQuillLib>(null);

  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        // If custom upload handler is provided, use it
        if (onImageUpload) {
          try {
            const imageUrl = await onImageUpload(file);
            const quill = quillRef.current?.getEditor();
            if (quill) {
              const range = quill.getSelection(true);
              quill.insertEmbed(range.index, 'image', imageUrl);
              quill.setSelection(range.index + 1, 0);
            }
          } catch (error) {
            console.error('Error uploading image:', error);
          }
        } else {
          // Convert to base64 if no upload handler provided
          const reader = new FileReader();
          reader.onload = (e) => {
            const quill = quillRef.current?.getEditor();
            if (quill && e.target?.result) {
              const range = quill.getSelection(true);
              quill.insertEmbed(range.index, 'image', e.target.result);
              quill.setSelection(range.index + 1, 0);
            }
          };
          reader.readAsDataURL(file);
        }
      }
    };
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    [onImageUpload]
  );

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'align',
    'link',
    'image',
  ];

  return (
    <div className={`${className} flex-auto`}>
      {label && (
        <div className='flex items-center mb-2'>
          {isLoading ? (
            <div className='input-card h-[15px] w-[72px]' />
          ) : (
            <label className='text-sm text-gray-600'>{label}</label>
          )}
          {isOptional && <span className='text-[#8B8D9D]'>(Optional)</span>}
          {isMandatory && (
            <span className='mandatory-field text-error'> *</span>
          )}
        </div>
      )}
      {isLoading ? (
        <div className='input-card h-[150px] w-full' />
      ) : (
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <div style={{ height }}>
              <ReactQuillLib
                ref={quillRef}
                id={id}
                theme='snow'
                modules={modules}
                formats={formats}
                placeholder={placeholder}
                readOnly={disabled}
                className={`${disabled ? 'opacity-60' : ''}`}
                style={{
                  height: `calc(${height} - 42px)`,
                  backgroundColor: 'white',
                }}
                value={(value as string) || ''}
                onChange={onChange}
                onBlur={onBlur}
              />
            </div>
          )}
        />
      )}
      {errorMessage && (
        <div
          className={`text-xs mt-2 flex items-center ${
            showErrorMessage ? 'text-error' : 'hide-error-message'
          }`}
        >
          <ErrorWarningIcon className='mr-2' />
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default ReactQuill;
